import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { capitalize } from 'lodash'
import {
  IconMessageReport,
  IconSparkles,
  IconMessagePlus,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react'
import { Field } from 'formik'

import { Divider } from '@/components/dividers/Divider'
import { Button } from '@/components/buttons/Button'
import { NotesPanel } from '@/components/notes/NotesPanel'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { CHANNEL_TYPE } from '@/utils/constants'
import { resolveDispute } from '@/reducers/callSearch/callSearch.actions'
import { Pill } from '@/components/pills/Pill'
import { Select } from '@/components/forms/formik'
import { findCriteriaInConfig, aiResponseToScore } from '@/utils/promptFeedback.helpers'
import { setCallExplorerData } from '@/reducers/callSearch/callSearch.redux'
import { Banner } from '@/components/banners/Banner'
import { PoweredByAI } from '@/components/ai/PoweredByAI'
import { CALL_EXPLORER_CRITERIA_FILTER_ENUMS, canUserSave } from './helpers'
import { QAScoreCriteriaTimestampButton } from './QAScoreCriteriaTimestampButton'
import { CriteriaFeedbackBanner } from './CriteriaFeedbackBanner'

const SCORE_STATUS_MAP = { 1: 'Passed', 0: 'Failed', [-1]: 'Skipped' }

function getStatusForScore(score) {
  return SCORE_STATUS_MAP[score] ?? ''
}

export const QAScoreEditCriteria = forwardRef(function QAScoreEditCriteria(
  { criteriaScore, score, callId, active, index, formikProps, callType = CHANNEL_TYPE.CALL },
  ref
) {
  const dispatch = useDispatch()

  const { agent_name: agentName, organization_id: organizationId } = useSelector(
    (state) => state.callSearch.callExplorer.metadata
  )
  const { selectedCriteriaFilter } = useSelector((state) => state.callSearch.callExplorer)
  const { targetScorecardConfig } = useSelector((state) => state.scorecards.data)
  const {
    user_id: userId,
    edit_qa: editRTQA,
    edit_qa_copilot: editQaCopilot,
  } = useSelector((state) => state.currentUser)

  const criteriaConfig = findCriteriaInConfig(
    targetScorecardConfig,
    criteriaScore.scorecard_criteria_id
  )
  const mustBePresent = criteriaConfig?.must_be_present ?? true

  const originalAiScore = useMemo(
    () => aiResponseToScore(criteriaScore.ai_response, mustBePresent, criteriaScore.score),
    [criteriaScore.id]
  )

  const [showDisputeButtons, setShowDisputeButtons] = useState(false)
  const [criteriaLoading, setCriteriaLoading] = useState(false)
  const [showNote, setShowNote] = useState(false)
  const [showFeedbackInput, setShowFeedbackInput] = useState(false)
  const [isAiExplanationExpanded, setIsAiExplanationExpanded] = useState(true)
  const [feedbackSkipped, setFeedbackSkipped] = useState(false)

  const disputeStatus = criteriaScore?.dispute?.status
  const isAutomatedScorecard = criteriaScore.criteria_type !== 'automated'
  const isAiCriteria = criteriaScore.criteria_type === 'ai'

  useEffect(() => {
    if (criteriaScore?.dispute?.status === 'pending') {
      setShowDisputeButtons(true)
    }

    // Show the notes panel by default when the user already has a note on
    // this criteria — keeps the same affordance as the legacy textarea
    // which auto-expanded on existing notes.
    const hasOwnNote = criteriaScore?.criteria_notes?.some(
      (note) => note.created_by === userId && !note.deleted
    )
    if (hasOwnNote) {
      setShowNote(true)
    }
  }, [])

  async function handleScoreChange(newScore) {
    await formikProps.setFieldValue(criteriaScore.uuid, newScore)

    if (!isAiCriteria) {
      return
    }

    const scoreMatches = Number(newScore) === Number(originalAiScore)
    setFeedbackSkipped(false)

    if (scoreMatches) {
      setShowFeedbackInput(false)
      formikProps.setFieldValue(`${criteriaScore.uuid}-feedback`, '')
      setIsAiExplanationExpanded(true)
    } else {
      setShowFeedbackInput(true)
      setIsAiExplanationExpanded(false)
    }
  }

  const toggleNote = () => {
    setShowNote((prev) => !prev)
  }

  const handleResolveDispute = async (criteriaScoreData, newStatus, newScore) => {
    setCriteriaLoading(true)
    const newCriteriaScore = {
      ...criteriaScoreData,
      dispute: {
        ...criteriaScoreData.dispute,
        manager_comment: '',
        status: newStatus,
        score: newScore,
      },
      score: newScore,
    }
    await dispatch(resolveDispute(newCriteriaScore, userId))
    await formikProps.setFieldValue(criteriaScoreData.uuid, newScore)
    setShowDisputeButtons(false)
    setCriteriaLoading(false)

    const currentFilter = selectedCriteriaFilter
    dispatch(
      setCallExplorerData({ selectedCriteriaFilter: CALL_EXPLORER_CRITERIA_FILTER_ENUMS.ALL })
    )
    dispatch(setCallExplorerData({ selectedCriteriaFilter: currentFilter }))
  }

  const handleFeedbackChange = (uuid, value) => {
    formikProps.setFieldValue(`${uuid}-feedback`, value)
  }

  const handleSkipFeedback = () => {
    setShowFeedbackInput(false)
    formikProps.setFieldValue(`${criteriaScore.uuid}-feedback`, '')
    setIsAiExplanationExpanded(true)
    setFeedbackSkipped(true)
  }

  function renderCriteriaNoteSection() {
    return (
      <NotesPanel
        anchor={{ noteType: 'scorecard_criteria_score', id: criteriaScore.id }}
        organizationId={organizationId}
        compact
        collapsible={false}
      />
    )
  }

  const canSave = canUserSave(editRTQA, editQaCopilot, criteriaScore.criteria_type)

  return (
    <div id={`criteria-${index}`} data-testid={criteriaScore.id}>
      <div className={classNames('line', { focused: true })} />
      <div
        className={classNames('criteria-container flex-space-between medium-gap', { active })}
        key={criteriaScore.id}
      >
        <div className="criteria-container-content">
          <div className="flex-space-between flex-align-start small-gap">
            <Pill
              dataTestId={`criteria-type-pill-${criteriaScore.id}`}
              magical={criteriaScore.criteria_type === 'ai'}
              caution={criteriaScore.manual}
              success={criteriaScore.criteria_type === 'automated'}
              outlined={criteriaScore.criteria_type === 'ai'}
              small
              className="mbs"
            >
              {criteriaScore.criteria_type === 'ai'
                ? 'AI'
                : capitalize(criteriaScore.criteria_type)}
            </Pill>
            <div className="flex-align-center small-gap">
              <Button
                square
                onClick={toggleNote}
                tooltip="Add a note"
                data-testid={`add-note-button-${criteriaScore.id}`}
              >
                <IconMessagePlus />
              </Button>

              {!showDisputeButtons && (
                <Field
                  tabIndex={-1}
                  innerRef={ref}
                  index={index}
                  name={criteriaScore.uuid}
                  component={Select}
                  isDisabled={!canSave}
                  placeholder=" -- "
                  options={[
                    { value: 1, label: 'Passed' },
                    { value: 0, label: 'Failed' },
                    { value: -1, label: 'N/A' },
                  ]}
                  isSearchable={false}
                  isClearable={false}
                  onChange={(option) => handleScoreChange(option.value)}
                />
              )}
            </div>
          </div>
          <h4 className="criteria-name">{criteriaScore.name}</h4>
          {criteriaScore.description && (
            <p data-testid="criterion-description" className="criteria-description">
              {criteriaScore.description}
            </p>
          )}
          {!criteriaScore.dispute && showNote && renderCriteriaNoteSection()}
          {isAutomatedScorecard && (
            <QAScoreCriteriaTimestampButton
              scoreId={score.id}
              callId={callId}
              criteriaScore={criteriaScore}
              callType={callType}
            />
          )}
          {criteriaScore.ai_explanation && (
            <>
              <div className="ai-explanation-section">
                <div className="ai-explanation-divider" />
                <div
                  onClick={() => setIsAiExplanationExpanded(!isAiExplanationExpanded)}
                  className={classNames('ai-explanation-header', {
                    expanded: isAiExplanationExpanded,
                  })}
                >
                  {isAiExplanationExpanded ? (
                    <IconChevronUp size={18} />
                  ) : (
                    <IconChevronDown size={18} />
                  )}
                  <h4 className="ai-explanation-title">
                    Score Reasoning
                    <Pill magical xs className="uppercase inline-flex">
                      <IconSparkles /> AI
                    </Pill>
                  </h4>
                </div>
                {isAiExplanationExpanded && (
                  <div className="ai-explanation-content">
                    <p className="ai-explanation-text">{criteriaScore.ai_explanation}</p>
                    <div className="ai-explanation-footer">
                      <PoweredByAI />
                    </div>
                  </div>
                )}
              </div>
              {canSave && !showFeedbackInput && isAiExplanationExpanded && !feedbackSkipped && (
                <Banner warning>
                  Disagree? You can change the score above and help us improve.
                </Banner>
              )}
            </>
          )}
          {showFeedbackInput && (
            <CriteriaFeedbackBanner
              criteriaId={criteriaScore.id}
              criteriaUuid={criteriaScore.uuid}
              originalAiScore={originalAiScore}
              currentScore={formikProps.values[criteriaScore.uuid] ?? criteriaScore.score}
              feedbackValue={formikProps.values[`${criteriaScore.uuid}-feedback`]}
              onFeedbackChange={handleFeedbackChange}
              onSkip={handleSkipFeedback}
            />
          )}
          {criteriaScore.dispute && <Divider />}
          {disputeStatus === 'pending' && (
            <Pill dataTestId={`criteria-dispute-pill-${criteriaScore.id}`} critical small>
              Pending Dispute
            </Pill>
          )}
          {criteriaScore.dispute && (
            <>
              <Banner
                emphasized
                icon={<IconMessageReport />}
                header={`Agent Dispute From: ${agentName}`}
              >
                <div className="dispute-status">
                  Original score:{' '}
                  {getStatusForScore(criteriaScore?.dispute?.previous_score ?? criteriaScore.score)}
                </div>
                <div className="mb">{criteriaScore.dispute.user_comment}</div>
                {showDisputeButtons && (
                  <div className="dispute-container">
                    <Button
                      critical
                      disabled={criteriaLoading || !canSave}
                      data-testid="reject-dispute-button"
                      onClick={async () => {
                        await handleResolveDispute(criteriaScore, 'declined', criteriaScore.score)
                      }}
                    >
                      Reject
                    </Button>

                    <ActionsDropdown
                      triggerLabel="Accept and change to..."
                      dataTestId="accept-dispute-button"
                      buttonProps={{ disabled: criteriaLoading || !canSave }}
                      actions={[
                        criteriaScore.score !== 1 && {
                          label: 'Passed',
                          onClick: () => handleResolveDispute(criteriaScore, 'accepted', 1),
                        },
                        criteriaScore.score !== 0 && {
                          label: 'Failed',
                          onClick: () => handleResolveDispute(criteriaScore, 'accepted', 0),
                        },
                        criteriaScore.score !== -1 && {
                          label: 'Skipped',
                          onClick: () => handleResolveDispute(criteriaScore, 'accepted', -1),
                        },
                      ].filter(Boolean)}
                    />
                  </div>
                )}
              </Banner>
              {renderCriteriaNoteSection()}
            </>
          )}
        </div>
      </div>
    </div>
  )
})
